// LibreOffice (MPL-2.0) compiled to WebAssembly, for Word/Excel/PowerPoint to
// PDF. About 60 MB compressed; downloaded once with the user’s consent.
import { absoluteUrl, vendor } from './vendor';

type Converter = import('@matbee/libreoffice-converter/browser').WorkerBrowserConverter;

let converter: Promise<Converter> | null = null;

/** Give up if the engine has not started after this long (slow or stuck devices). */
const START_TIMEOUT_MS = 4 * 60 * 1000;

class EngineTimeoutError extends Error {
  constructor() {
    super('LibreOffice did not respond in time');
    this.name = 'EngineTimeoutError';
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout: () => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(new EngineTimeoutError());
    }, ms);
    promise.then(
      (v) => (clearTimeout(timer), resolve(v)),
      (e) => (clearTimeout(timer), reject(e)),
    );
  });
}

let progressListener: ((percent: number, message: string) => void) | null = null;

export function officeSupported(): boolean {
  return typeof SharedArrayBuffer !== 'undefined' && (self as unknown as { crossOriginIsolated?: boolean }).crossOriginIsolated === true;
}

export function getOffice(onProgress?: (percent: number, message: string) => void): Promise<Converter> {
  progressListener = onProgress ?? null;
  converter ??= (async () => {
    const { WorkerBrowserConverter } = await import('@matbee/libreoffice-converter/browser');
    const base = absoluteUrl(vendor('office').base);
    const c = new WorkerBrowserConverter({
      sofficeJs: `${base}soffice.js`,
      sofficeWasm: `${base}soffice.wasm`,
      sofficeData: `${base}soffice.data`,
      sofficeWorkerJs: `${base}soffice.worker.js`,
      browserWorkerJs: `${base}browser.worker.global.js`,
      onProgress: (info: { percent: number; message: string }) => progressListener?.(info.percent, info.message),
    });
    await withTimeout(c.initialize(), START_TIMEOUT_MS, () => void shutdown(c));
    return c;
  })();
  converter.catch(() => {
    converter = null;
  });
  return converter;
}

/** A single conversion normally takes seconds; beyond this the engine is assumed stuck. */
const CONVERT_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Stops an engine. destroy() asks the worker to shut down and waits for its
 * reply, which a stuck worker never sends: terminate it directly after a while.
 */
async function shutdown(c: Converter): Promise<void> {
  const stopped = c.destroy().then(
    () => true,
    () => false,
  );
  const clean = await Promise.race([stopped, new Promise<false>((resolve) => setTimeout(() => resolve(false), 5000))]);
  if (!clean) (c as unknown as { worker?: Worker | null }).worker?.terminate();
}

async function resetOffice(): Promise<void> {
  const current = converter;
  converter = null;
  const c = await current?.catch(() => null);
  if (c) await shutdown(c);
}

export async function officeToPdf(bytes: Uint8Array, fileName: string, onProgress?: (percent: number, message: string) => void): Promise<Uint8Array> {
  // LibreOffice's WebAssembly threads can occasionally stall on busy devices:
  // restart the engine once before giving up.
  for (let attempt = 0; ; attempt++) {
    const c = await getOffice(onProgress);
    try {
      const result = await withTimeout(c.convert(bytes, { outputFormat: 'pdf' }, fileName), CONVERT_TIMEOUT_MS, () => undefined);
      return result.data;
    } catch (err) {
      if (!(err instanceof EngineTimeoutError) || attempt >= 1) throw err;
      await resetOffice();
    }
  }
}
